export type AgentStarterPromptIcon = 'compass' | 'play' | 'wrench' | 'cpu';

export interface AgentStarterPrompt {
  id: string;
  iconKey: AgentStarterPromptIcon;
  /** Short title shown as the chip headline. */
  label: string;
  /** One-line subtitle rendered under the label -- describes the ask, not the full prompt. */
  description: string;
  /**
   * Rich multi-line prompt sent to the agent. This is intentionally
   * more detailed than the visible chip so the backend receives a
   * strong first turn while the chat transcript stays compact.
   */
  prompt: string;
}

export interface AgentStarterPromptDisplay {
  kind: 'starter_prompt';
  id: string;
  iconKey: AgentStarterPromptIcon;
  label: string;
  description: string;
}

export const DEFAULT_STARTERS: AgentStarterPrompt[] = [
  {
    id: 'explore-project',
    iconKey: 'compass',
    label: 'Explore the project',
    description: 'Map the project context, then suggest Antioch-native next steps',
    prompt: [
      'First familiarize yourself with this project and the surrounding Antioch context before doing any task work.',
      '',
      'Read the relevant project guidance, folder structure, scenarios, assets, autonomy code, and any local notes that explain how this workspace is meant to be used. Summarize what kind of project this is, where the important implementation and scenario surfaces live, what conventions or constraints matter, and what feedback loops already exist or should exist.',
      '',
      'Then propose a small set of high-leverage directions I could take next, framed as concrete Antioch workflows. Do not start implementing or running anything yet. End by asking me which specific target, scenario, asset, bug, or workflow I want you to focus on first.',
    ].join('\n'),
  },
  {
    id: 'run-scenario',
    iconKey: 'play',
    label: 'Run a simulation scenario',
    description: 'Scan available scenarios first, then ask which run to launch',
    prompt: [
      'First familiarize yourself with this project and its simulation scenario surface before trying to run anything.',
      '',
      'Inspect the project guidance, scenario registry or scenario folders, launch scripts, assets, telemetry expectations, and any recent notes that explain how scenarios in this project are supposed to be executed. Summarize the viable scenario candidates, what each one appears to test, what evidence or telemetry would prove it worked, and any risks such as missing assets, auth, livestream requirements, or long startup time.',
      '',
      'Do not assume a scenario target. After the scan, ask me which concrete scenario, mode, and verification goal I want to run. When I choose one, use the existing project workflow, launch it in the appropriate mode, and report live progress plus the fail-loud success signal.',
    ].join('\n'),
  },
  {
    id: 'onboard-system',
    iconKey: 'wrench',
    label: 'Onboard a new system',
    description: 'Turn available assets or logs into a validated digital twin plan',
    prompt: [
      'First familiarize yourself with this project and any available context for the hardware or system before adding files or converting assets.',
      '',
      'Inspect the relevant guidance, asset folders, CAD/URDF/USD files, drivers, datasheets, logs, calibration notes, and existing scenarios. Summarize what is already present, what is missing, which coordinate frames, scale assumptions, collision geometry, sensors, and articulation details need validation, and what the smallest honest simulation feedback loop should be for onboarding.',
      '',
      'Then outline the high-level onboarding path and ask me to specify the concrete target system or asset set to onboard. Do not invent placeholder hardware or silently substitute assets; wait for my target before making changes.',
    ].join('\n'),
  },
  {
    id: 'upgrade-system',
    iconKey: 'cpu',
    label: 'Upgrade software or hardware',
    description: 'Use simulation as the feedback loop to iteratively validate choices and changes',
    prompt: [
      'First familiarize yourself with this project, the current system design, and the relevant simulation feedback loops before recommending or changing anything.',
      '',
      'Inspect the project guidance, scenario coverage, assets, control/perception/planning code, dependency manifests, hardware interfaces, telemetry, and any notes that describe known limitations or upgrade goals. Summarize what can be safely evaluated in simulation, what metrics would validate an upgrade, and what risks would need a fail-loud test before touching production-facing behavior.',
      '',
      'Then help me plan an iterative software or hardware upgrade workflow that uses simulation as the feedback loop for comparing choices and validating each change. Ask me to specify the concrete upgrade target -- component, dependency, controller, sensor, robot part, or hardware alternative -- before implementing or running experiments.',
    ].join('\n'),
  },
];

export function starterPromptDisplay(starter: AgentStarterPrompt): AgentStarterPromptDisplay {
  return {
    kind: 'starter_prompt',
    id: starter.id,
    iconKey: starter.iconKey,
    label: starter.label,
    description: starter.description,
  };
}
